/**
 * Copyright © 2026 Adnuntius AS.
 */
import {firstValueFrom} from 'rxjs';
import {DOCUMENT, Component, ElementRef, Inject, OnInit, signal} from '@angular/core';
import * as _ from 'lodash';

import {CreativeViewer} from '../../../components/util/creative-viewer';
import {normalizePreviewLanguageSelection, PREVIEW_LANGUAGE_OPTIONS, resolvePreviewLanguageCode} from '../../creative/ad/preview-language';
import {collateCreativePreviewLineItemData} from '../../creative/ad/preview-request-data';
import template from './political-overlay-preview-page.component.html';
import {SIMPLE_PAGE_IMPORTS} from './simple-page-imports';
import {CreativeApiService} from '../../creative/creative-api.service';
import {LayoutApiService} from '../../admin/layout/layout-api.service';
import {WINDOW} from '../../angular-platform-providers';
import {PoliticalAdsApiService} from './political-ads-api.service';
import {CreativePreviewApiService} from '../../creative/creative-preview-api.service';

const EMPTY_TEMPLATE_MESSAGE = '<!-- Default render-dsa template could not be loaded. -->';

@Component({
  standalone: true,
  imports: SIMPLE_PAGE_IMPORTS,
  selector: 'adn-political-overlay-preview-page',
  template
})
export class PoliticalOverlayPreviewPageComponent implements OnInit {
  private readonly formState = signal({
    creativeId: '',
    template: ''
  });
  private readonly templateLoadingState = signal(true);
  private readonly loadingState = signal(false);
  private readonly hasPreviewState = signal(false);

  get form(): {creativeId: string; template: string} {
    return this.formState();
  }

  set form(value: {creativeId: string; template: string}) {
    this.formState.set(value);
  }

  get templateLoading(): boolean {
    return this.templateLoadingState();
  }

  set templateLoading(value: boolean) {
    this.templateLoadingState.set(value);
  }

  get loading(): boolean {
    return this.loadingState();
  }

  set loading(value: boolean) {
    this.loadingState.set(value);
  }

  get hasPreview(): boolean {
    return this.hasPreviewState();
  }

  set hasPreview(value: boolean) {
    this.hasPreviewState.set(value);
  }
  previewLanguageOptions = PREVIEW_LANGUAGE_OPTIONS;
  previewLanguage = 'browser';
  previewHostId = 'politicalOverlayPreviewHost';
  previewTargetId = 'politicalOverlayPreviewTarget';
  previewRenderCount = 0;
  error = '';
  errorMessage: any = null;
  publicError: string | null = null;
  codemirrorReady = false;
  derivedPreviewParams: any = null;
  previewUrl = '';

  constructor(
    @Inject(DOCUMENT) private readonly document: any,
    private readonly elementRef: ElementRef<HTMLElement>,
    @Inject(WINDOW) private readonly window: any,
    @Inject(CreativeApiService) private readonly Creative: CreativeApiService,
    @Inject(LayoutApiService) private readonly Layout: LayoutApiService,
    private readonly creativePreviewApi: CreativePreviewApiService,
    private readonly politicalAdsApi: PoliticalAdsApiService
  ) {
  }

  ngOnInit(): void {
    setTimeout(() => {
      this.codemirrorReady = true;
    }, 0);

    firstValueFrom(this.politicalAdsApi.getRenderDsaTemplate()).then((response: any) => {
      this.form = {...this.form, template: this.extractTemplate(response) || EMPTY_TEMPLATE_MESSAGE};
    }).catch((error: any) => {
      this.form = {...this.form, template: EMPTY_TEMPLATE_MESSAGE};
      this.error = _.get(error, ['data'], 'Unable to load the render-dsa template.');
    }).finally(() => {
      this.templateLoading = false;
    });
  }

  getSelectedLanguageCode(): string {
    return resolvePreviewLanguageCode(this.previewLanguage, this.window.navigator);
  }

  makeOverlayRenderTemplate(layoutRenderTemplate: string): string {
    if ((layoutRenderTemplate || '').indexOf('{{{politicalOverlay}}}') < 0) {
      throw new Error('The selected creative needs a political layout. Its render template does not contain {{{politicalOverlay}}}.');
    }
    const overlayScript = '<template id="politicalOverlay">\n'
      + (this.form.template || '')
      + '\n</template>\n';
    return (layoutRenderTemplate || '').replace('{{{politicalOverlay}}}', overlayScript + '{{{politicalOverlay}}}');
  }

  resetPreviewTarget(): HTMLElement | null {
    this.previewRenderCount++;
    this.previewTargetId = 'politicalOverlayPreviewTarget-' + this.previewRenderCount;

    const previewHost = this.elementRef.nativeElement.querySelector('#' + this.previewHostId);
    if (!previewHost) {
      return null;
    }

    previewHost.innerHTML = '';
    const previewTarget = this.document.createElement('div');
    previewTarget.setAttribute('id', this.previewTargetId);
    previewHost.appendChild(previewTarget);
    return previewTarget;
  }

  render(): void {
    this.previewLanguage = normalizePreviewLanguageSelection(this.previewLanguage);
    this.error = '';
    this.errorMessage = null;
    this.publicError = null;
    this.loading = true;
    this.hasPreview = true;

    this.resetPreviewTarget();

    firstValueFrom(this.Creative.getHydratedPreview(this.form.creativeId)).then((creative: any) => {
      const previewParams = {
        creativeId: creative.id,
        networkId: _.get(creative, ['network', 'id']),
        creativeWidth: creative.width,
        creativeHeight: creative.height,
        layoutType: _.get(creative, ['layout', 'ogLayoutType']) || _.get(creative, ['layout', 'layoutType']) || _.get(creative, ['layout', 'detectedCategory']) || 'IMAGE'
      };
      _.assign(previewParams, collateCreativePreviewLineItemData(creative));

      this.derivedPreviewParams = previewParams;
      this.previewUrl = 'POST ' + '/preview';

      const totalAssets = _.get(creative, 'assets', []).concat(_.get(creative, 'libraryCreativeAssets', []));
      const layoutParameters: Record<string, any> = {};
      _.forEach(creative.layoutParameters, (value: any, key: string) => {
        if (value && value.id && !value.content) {
          layoutParameters[key] = _.pick((_.find(totalAssets, ['id', value.id]) || {}), ['cdnId', 'previewCdnId', 'transcode', 'width', 'height', 'content', 'duration', 'bps']);
        } else {
          layoutParameters[key] = value;
        }
      });

      return firstValueFrom(this.Layout.get(_.get(creative, ['layout', 'id']))).then((layout: any) => {
        const renderTemplate = this.makeOverlayRenderTemplate(layout.renderTemplate);
        const apiPreviewData: Record<string, any> = {
          creativeWidth: previewParams.creativeWidth,
          creativeHeight: previewParams.creativeHeight,
          layoutParameters: layoutParameters,
          layoutId: layout.id,
          renderTemplate: renderTemplate
        };
        _.assign(apiPreviewData, _.pick(previewParams, ['lineItemId', 'lineItemCode']));

        return firstValueFrom(this.creativePreviewApi.render(apiPreviewData)).then((data: any) => {
          const previewContainer = this.resetPreviewTarget();
          new CreativeViewer(this.document).viewCreative(previewContainer, {
            creativeContent: data.document,
            creativeWidth: previewParams.creativeWidth,
            creativeHeight: previewParams.creativeHeight,
            layoutType: previewParams.layoutType,
            showBorder: true,
            allowScript: true,
            dataLang: this.getSelectedLanguageCode()
          });
        });
      });
    }).catch((error: any) => {
      this.hasPreview = false;
      this.error = _.get(error, 'message') || _.get(error, ['data', 'text'], 'Unable to generate the political overlay preview.');
    }).finally(() => {
      this.loading = false;
    });
  }

  private extractTemplate(response: any): string {
    if (_.isString(response)) {
      return response;
    }
    return _.get(response, 'template')
      || _.get(response, ['result', 'template'])
      || _.get(response, ['data', 'template'])
      || '';
  }
}
